import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

let pages = [];
const adminPages = [
  'Employees',
  'Clients',
  'Sales',
  'Business Tax',
  'Payroll',
  'Reports',
  'Doc Gen'
];
const employeePages = ['Clients', 'Sales', 'BusinessTax', 'Payroll', 'Reports', 'Doc Gen'];
const user = JSON.parse(localStorage.getItem('user') || 'null');
if (user && user.role === 'admin') {
  pages = adminPages;
} else if (user && user.role === 'semiAdmin') {
  pages = employeePages;
}

const settings = ['Logout'];

interface NavbarProps {
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onLogout }) => {
  const theme = useTheme();
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    onLogout();
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handlePageClick = (page: any) => {
    handleCloseNavMenu();

    switch (page) {
      case 'Employees':
        navigate('/employees');
        break;

      case 'Clients':
        navigate('/clients');
        break;

      case 'Sales':
        navigate('/sales');
        break;
      case 'Business Tax':
        navigate('/businessTax');
        break;

      case 'Payroll':
        navigate('/payroll');
        break;

      case 'Reports':
        navigate('/reports');
        break;

      case 'Doc Gen':
        navigate('/document-generator');
        break;
    }
  };

  // Helper to determine active state
  const isActive = (page: string) => {
    const path = location.pathname;
    switch (page) {
      case 'Employees': return path.startsWith('/employees');
      case 'Clients': return path.startsWith('/clients');
      case 'Sales': return path.startsWith('/sales');
      case 'Business Tax': return path.startsWith('/businessTax');
      case 'Payroll': return path.startsWith('/payroll');
      case 'Reports': return path.startsWith('/reports');
      case 'Doc Gen': return path.startsWith('/document-generator') || path.startsWith('/templates');
      default: return false;
    }
  };

  return (
    <AppBar position="sticky" color="default" sx={{ borderRadius: 0, boxShadow: theme.shadows[1] }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Box for Web View Starts Here */}
          <Box
            sx={{
              width: '100%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{
                  mr: 2,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 800,
                  letterSpacing: '.1rem',
                  color: theme.palette.primary.main,
                  textDecoration: 'none'
                }}
              >
                AK Fintax
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 2 }}>
              {pages.map((page) => (
                <Button
                  key={page}
                  onClick={() => handlePageClick(page)}
                  sx={{
                    my: 2,
                    color: isActive(page) ? theme.palette.primary.main : 'text.secondary',
                    fontWeight: isActive(page) ? 700 : 500,
                    display: 'block',
                    backgroundColor: isActive(page) ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  {page}
                </Button>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body1"
                sx={{ display: { xs: 'none', lg: 'block' }, fontWeight: 600, color: 'text.secondary' }}
              >
                {user && user.firstName}
              </Typography>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    alt={user && user.firstName}
                    src="/static/images/avatar/2.jpg" // Placeholder or actual image path
                    sx={{ bgcolor: theme.palette.primary.main }}
                  >
                    {user && user.firstName ? user.firstName.charAt(0) : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem
                    key={setting}
                    onClick={setting === 'Logout' ? handleLogout : handleCloseUserMenu}
                  >
                    <Typography textAlign="center">{setting}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          {/* Box for Web View Ends Here */}

          {/* Box for Mobile View Starts Here */}
          <Box
            sx={{
              width: '100%',
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' }
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page} onClick={() => handlePageClick(page)}>
                  <Typography textAlign="center">{page}</Typography>
                </MenuItem>
              ))}
            </Menu>

            <Typography
              variant="h6"
              noWrap
              component="a"
              href="/"
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              AK Fintax
            </Typography>

            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    alt={user && user.firstName}
                    sx={{ bgcolor: theme.palette.primary.main }}
                  >
                    {user && user.firstName ? user.firstName.charAt(0) : 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem
                    key={setting}
                    onClick={setting === 'Logout' ? handleLogout : handleCloseUserMenu}
                  >
                    <Typography textAlign="center">{setting}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          {/* Box for Mobile View Ends Here */}
        </Toolbar >
      </Container >
    </AppBar >
  );
};

export default Navbar;
